import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Page, Card, Button, Modal, Tabs } from "@project-mgmt/ui";
import { EmptyState } from "../components/ErrorState";
import { WorkspaceMetrics } from "../components/WorkspaceMetrics";
import { routes } from "../utils/routes";
import type { WorkspaceRouteParams } from "../types/routes";
import { useWorkspaceDetails, WorkspaceSettings } from "@project-mgmt/feature-workspaces";
import { useWorkspacePermissions } from "../hooks/useSimplePermissions";
import {
  type Project,
  ProjectList,
  ProjectForm,
  useProjects,
  useCreateProject,
  useUpdateProject,
  useArchiveProject,
} from "@project-mgmt/feature-projects";
import { useAuth } from "@project-mgmt/feature-auth";
import { NotificationCenter, useUnreadNotificationsCount } from "@project-mgmt/feature-notifications";
import { WorkspaceFlowViz } from "@project-mgmt/feature-flow-visualization";
import "@project-mgmt/feature-flow-visualization/dist/styles/flow.css";

/**
 * WorkspaceDetailPage
 *
 * Displays workspace details and provides navigation to:
 * - Projects
 * - Tasks
 * - Files
 * - Notifications
 */
export function WorkspaceDetailPage() {
  const navigate = useNavigate();
  const { workspaceId } = useParams<WorkspaceRouteParams>();
  const { data, isLoading, refetch: refetchWorkspace } = useWorkspaceDetails(workspaceId ?? "");
  const permissions = useWorkspacePermissions();
  const { user } = useAuth();
  const currentUserId = user?.id || "";

  const { data: projectsData, isLoading: isProjectLoading, error } = useProjects(workspaceId ?? "");
  const projects = projectsData?.projects || [];

  const [activeTab, setActiveTab] = useState("projects");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const { trigger: createProject, isLoading: isCreating, error: createError } = useCreateProject();

  // Update project mutation
  const {
    trigger: updateProject,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateProject(selectedProject?.id ?? "", workspaceId ?? "");

  // Archive project mutation
  const { trigger: archiveProject, isLoading: isArchiving } = useArchiveProject(workspaceId ?? "");

  const workspace = data?.workspace;
  const members = data?.members || [];

  // Get unread notifications count for badge
  const { data: unreadData } = useUnreadNotificationsCount(workspaceId);
  const unreadCount = unreadData?.count ?? 0;

  if (!workspaceId) {
    return <EmptyState message="Workspace ID not found." />;
  }

  if (isLoading) {
    return (
      <Container size="xl">
        <Page className="brand-page">
          <div className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"></div>
            </div>
            <p className="text-textSecondary">Loading workspace...</p>
          </div>
        </Page>
      </Container>
    );
  }

  if (!workspace) {
    return <EmptyState message="Workspace not found." />;
  }

  const handleProjectSelect = (project: Project) => {
    if (workspaceId) {
      navigate(routes.projectTasks(workspaceId, project.id));
    }
  };

  // Handle project creation
  const handleCreateProject = async (projectData: any) => {
    try {
      const newProject = await createProject({
        ...projectData,
        workspaceId: workspace?.id,
      });

      setShowCreateForm(false);

      // Auto-select the new project
      if (newProject) {
        handleProjectSelect(newProject);
      }
    } catch (error) {
      console.error("Failed to create project:", error);
    }
  };

  // Handle project edit
  const handleEditProject = (project: Project) => {
    setSelectedProject(project);
    setShowEditForm(true);
  };

  // Handle project update
  const handleUpdateProject = async (projectData: any) => {
    if (!selectedProject) return;

    try {
      await updateProject(projectData);
      setShowEditForm(false);
      setSelectedProject(null);
    } catch (error) {
      console.error("Failed to update project:", error);
    }
  };

  // Handle project archive - show confirmation modal
  const handleArchiveProject = (project: Project) => {
    setSelectedProject(project);
    setShowArchiveConfirm(true);
  };

  // Confirm and execute archive
  const confirmArchiveProject = async () => {
    if (!selectedProject) return;

    setShowArchiveConfirm(false);

    try {
      await archiveProject(selectedProject.id);
      setSelectedProject(null);
    } catch (error) {
      console.error("Failed to archive project:", error);
      setSelectedProject(null);
    }
  };

  return (
    <Container size="xl">
      <Page className="brand-page">
        {/* Workspace Header */}
        <Card className="brand-page-hero mb-6 p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="brand-page-kicker">Workspace Command Center</p>
              <div className="mb-2 flex items-center gap-3">
                <h1 className="brand-page-title">{workspace.name}</h1>
                <span
                  className={`rounded-full px-3 py-1 text-sm ${
                    workspace.isOwner ? "brand-badge-owner" : "brand-badge-neutral"
                  }`}
                >
                  {workspace.isOwner ? "Owner" : workspace.role}
                </span>
              </div>
              {workspace.description && <p className="text-textSecondary mb-4 text-lg">{workspace.description}</p>}
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span>Created {new Date(workspace.createdAt).toLocaleDateString()}</span>
                {workspace.updatedAt && <span>Updated {new Date(workspace.updatedAt).toLocaleDateString()}</span>}
              </div>
            </div>
          </div>
        </Card>

        {/* Workspace Metrics */}
        <div className="brand-section-card mb-6 p-4 sm:p-6">
          <WorkspaceMetrics workspaceId={workspaceId} />
        </div>

        {/* Tabs */}
        <div className="brand-section-card mb-6 p-4 sm:p-5">
          <Tabs
            tabs={[
            {
              id: "projects",
              label: "Projects",
              icon: (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              ),
              badge: projects.length,
              content: (
                <Card className="p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-textPrimary text-xl font-semibold">Projects</h2>
                    <Button onClick={() => setShowCreateForm(true)} className="touch-target">
                      <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Create Project
                    </Button>
                  </div>
                  <ProjectList
                    projects={projects}
                    loading={isProjectLoading || isArchiving}
                    error={error?.message || null}
                    onProjectView={handleProjectSelect}
                    onProjectEdit={handleEditProject}
                    onProjectArchive={handleArchiveProject}
                    emptyMessage={
                      permissions.canCreateProject
                        ? "No projects yet. Create your first project to get started."
                        : "No projects available in this workspace."
                    }
                  />
                </Card>
              ),
            },
            {
              id: "flow",
              label: "Flow View",
              icon: (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z"
                  />
                </svg>
              ),
              content: (
                <div className="relative" style={{ height: "calc(100vh - 400px)", minHeight: "600px" }}>
                  <WorkspaceFlowViz
                    workspaceId={workspaceId}
                    height="100%"
                    width="100%"
                    onNodeClick={(node) => {
                      console.log("Node clicked:", node);
                      // Future: Navigate to entity detail page based on node type
                    }}
                  />
                </div>
              ),
            },
            {
              id: "notifications",
              label: "Notifications",
              icon: (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
              ),
              badge: unreadCount || undefined,
              content: (
                <Card className="p-6">
                  <div className="mb-4">
                    <h2 className="text-textPrimary text-xl font-semibold">Notifications</h2>
                    <p className="text-sm text-gray-600">Stay updated with workspace activity</p>
                  </div>
                  <NotificationCenter
                    workspaceId={workspaceId}
                    maxHeight="max-h-[600px]"
                    onNotificationClick={(notification) => {
                      // Handle notification click - navigate to relevant page
                      if (notification.taskId && notification.projectId) {
                        navigate(routes.task(workspaceId, notification.projectId, notification.taskId));
                      } else if (notification.projectId) {
                        navigate(routes.projectTasks(workspaceId, notification.projectId));
                      }
                    }}
                  />
                </Card>
              ),
            },
            {
              id: "settings",
              label: "Settings",
              icon: (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              ),
              badge: members.filter((m) => m.status === "pending").length || undefined,
              content: (
                <WorkspaceSettings
                  workspace={workspace}
                  members={members}
                  currentUserId={currentUserId}
                  onWorkspaceUpdate={() => refetchWorkspace()}
                  onWorkspaceDelete={() => navigate(routes.workspaces(), { state: { skipAutoRestore: true } })}
                />
              ),
            },
          ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            variant="underline"
            size="md"
          />
        </div>

        {/* Create Project Modal */}
        <Modal isOpen={showCreateForm} onClose={() => setShowCreateForm(false)} className="max-w-2xl">
          <ProjectForm
            onSubmit={handleCreateProject}
            onCancel={() => setShowCreateForm(false)}
            loading={isCreating}
            error={createError?.message || null}
          />
        </Modal>

        {/* Edit Project Modal */}
        <Modal
          isOpen={showEditForm}
          onClose={() => {
            setShowEditForm(false);
            setSelectedProject(null);
          }}
          className="max-w-2xl"
        >
          <ProjectForm
            project={selectedProject || undefined}
            onSubmit={handleUpdateProject}
            onCancel={() => {
              setShowEditForm(false);
              setSelectedProject(null);
            }}
            loading={isUpdating}
            error={updateError?.message || null}
          />
        </Modal>

        {/* Archive Confirmation Modal */}
        <Modal
          isOpen={showArchiveConfirm}
          onClose={() => {
            setShowArchiveConfirm(false);
            setSelectedProject(null);
          }}
          className="max-w-md"
        >
          <div className="p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Archive Project</h3>
                <p className="text-sm text-gray-500">This action can be undone later</p>
              </div>
            </div>

            <p className="mb-6 text-gray-700">
              Are you sure you want to archive <span className="font-semibold">"{selectedProject?.name}"</span>? The
              project and its tasks will be hidden from active views.
            </p>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowArchiveConfirm(false);
                  setSelectedProject(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmArchiveProject}
                className="flex-1 bg-red-600 text-white hover:bg-red-700"
                disabled={isArchiving}
              >
                {isArchiving ? "Archiving..." : "Archive Project"}
              </Button>
            </div>
          </div>
        </Modal>
      </Page>
    </Container>
  );
}
